import { sourTwMerge } from '@plaidev/sour-react';
import { type FocusEvent, type PropsWithChildren, type ReactNode, useEffect } from 'react';

export const GNAVI_COLLAPSED_WIDTH = '64px';
export const GNAVI_EXPANDED_WIDTH = '224px';

/**
 * v1 (auth-lib-front Sidebar) 互換で `<html>` に CSS カスタムプロパティ `--gnav-width` を書き込む。
 * 利用側 (issues の LiveViewerPopup 等) が `calc(100svw - var(--gnav-width))` で参照する。
 *
 * 値は常に折りたたみ幅 `GNAVI_COLLAPSED_WIDTH` で固定する。v2 の展開は absolute オーバーレイ表示で
 * コンテンツを押し出さないため、レイアウト幅は expanded state に連動しない。
 *
 * 戻り値はクリーンアップ関数 (書き込んだ変数を削除する)。SSR で document に触れないよう
 * 呼び出しは useEffect 内に限定すること。
 */
export function writeGnavWidthVar(doc: Document = document): () => void {
  doc.documentElement.style.setProperty('--gnav-width', GNAVI_COLLAPSED_WIDTH);
  return () => {
    doc.documentElement.style.removeProperty('--gnav-width');
  };
}

export const NAVI_FIXED_THEME_VARS = {
  fontSize: '16px',
  '--spacing': '4px',
  '--text-xs': '12px',
  '--text-xs--line-height': '16px',
  '--text-sm': '14px',
  '--text-sm--line-height': '20px',
  '--text-base': '16px',
  '--text-base--line-height': '24px',
  '--radius-sm': '2px',
  '--radius-md': '6px',
  '--radius-lg': '8px',
} as React.CSSProperties;

export type SidebarShellProps = PropsWithChildren<{
  expanded: boolean;
  onPointerEnter: () => void;
  onPointerLeave: () => void;
  onFocusWithin: () => void;
  onFocusLeave: () => void;
  className?: string;
  expandedWidth?: string;
}>;

const isFocusMovingWithin = (event: FocusEvent<HTMLElement>): boolean => {
  const related = event.relatedTarget;
  return related instanceof Node && event.currentTarget.contains(related);
};

/**
 * v1 auth-lib-front Sidebar と同様、折りたたみ時はスペーサーでレイアウト幅を確保し、
 * ナビ本体は absolute で hover 展開する。
 *
 * ポインタ hover とフォーカスは別 state で扱う（blur で hover が消えないようにする）。
 */
export function SidebarShell({
  expanded,
  onPointerEnter,
  onPointerLeave,
  onFocusWithin,
  onFocusLeave,
  className,
  expandedWidth = GNAVI_EXPANDED_WIDTH,
  children,
}: SidebarShellProps): ReactNode {
  // v1 互換: マウント中は <html> に --gnav-width を書き込み、アンマウントで削除する。
  useEffect(() => writeGnavWidthVar(), []);

  const handleFocus = (event: FocusEvent<HTMLElement>) => {
    if (!isFocusMovingWithin(event)) {
      onFocusWithin();
    }
  };

  const handleBlur = (event: FocusEvent<HTMLElement>) => {
    if (!isFocusMovingWithin(event)) {
      onFocusLeave();
    }
  };

  return (
    <>
      <div className="shrink-0 transition-all duration-200" style={{ width: GNAVI_COLLAPSED_WIDTH }} />
      <nav
        className={sourTwMerge('absolute z-10 flex h-screen shrink-0 flex-col overflow-hidden transition-all duration-200', className)}
        style={{ width: expanded ? expandedWidth : GNAVI_COLLAPSED_WIDTH, ...NAVI_FIXED_THEME_VARS }}
        onMouseEnter={onPointerEnter}
        onMouseLeave={onPointerLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        aria-label="グローバルナビゲーション"
      >
        {children}
      </nav>
    </>
  );
}
